import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { TaskListItems, TaskPage, TaskPageData } from "../task";
import { AbortDictionary, getApiCall, getFileApiCall, postApiCall, postFileApiCall } from "./tokenService";
import { IPagePanel } from "../common/pagePanel";
import { ISavedChartPanel } from "../chart/savedChartPanel";
import { FrontToDoItems } from "../front/frontToDoItems";
import { ReportItems } from "../report/reportItems";
import { UnitNodePermissions } from "../unit/unitNodePermissions";
import { ModuleUnits } from "../unit/moduleUnits";
import { IReportParameterValue } from "../report/reportParameterValue";
import { ReportGenerated2 } from "../report/reportGenerated2";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { TaskStateEdit } from "../taskState/taskStateEdit";
import { TaskStateListItems } from "../taskState/taskStateListItems";
import { TaskPriorityListItems } from "../taskPriority/taskPriorityListItems";
import { TaskPriorityEdit } from "../taskPriority/taskPriorityEdit";
import { TaskTypeListItems } from "../taskType/taskTypeListItems";
import { TaskTypeEdit } from "../taskType/taskTypeEdit";
import { TaskEdit } from "../task/taskdit";
import { UserListItems } from "../user/userListItems";
import { SaveSuccessItems } from "ui/Scripts/source/models/common/saveSuccessItems";
import { TaskDataRowEdit } from "../task/taskDataRowEdit";
import { TaskDataRowListItems } from "../task/taskDataRowListItems";
import { UnitModuleSaveResult } from "../unit/unitModuleSaveResult";
import { TaskTypeNew } from "../taskType/taskTypeNew";
import { CalendarTaskListItems } from "ui/Scripts/source/models/calendarTask/calendarTaskListItems";
import { CalendarTaskEdit } from "ui/Scripts/source/models/calendarTask/calendarTaskEdit";
import { TaskTypePrintHtml } from "../taskType/taskTypePrintHtml";
import { TaskDataRowDefaultValues } from "ui/Scripts/source/models/task/taskDataRowDefaultValues";
import { TaskEditTaskTypeData } from "../task/taskEditTaskTypeData";
import { TaskEditUnitData } from "../task/taskEditUnitData";
import { TaskHandlerType } from "ui/Scripts/source/models/enums";
import { TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { Base } from "ui/Scripts/source/framework/base";
import { DashboardChartData } from "../report/dashboardChartData";
import { SharedPanels } from "../panel/sharedPanels";
import { ISharedPanelSave } from "../panel/sharedPanel";
import { UserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";

//Abort
const commonAborts = new AbortDictionary();
const reportAborts = new AbortDictionary();
const chartAborts = new AbortDictionary();
const statisticAborts = new AbortDictionary();

// #region TaskPage
export const getTaskPage = (): Promise<TaskPage> => {
    return getApiCall("api/task", TaskPage);
};

export const getTaskPageData = (companyId: string): Promise<TaskPageData> => {
    return getApiCall("api/task/pagedata?id=" + companyId, TaskPageData);
};

export const getTaskListItems = (companyId: string, pageSize: number, page: number, thrashBin: boolean, filter: string, unitIds: string[],
    stateIds: string[], priorityIds: string[], taskTypeIds: string[], handlerUserCodes: string[], personInChargeUserCodes: string[],
    taskTypeHeaderFields: string, taskTypeFields: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<TaskListItems> => {
    reportAborts.abortAll();
    chartAborts.abortAll();
    statisticAborts.abortAll();
    return postApiCall("api/task/list", TaskListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        thrashBin,
        filter: filter || "",
        unitIds,
        stateIds,
        priorityIds,
        taskTypeIds,
        handlerUserCodes,
        personInChargeUserCodes,
        taskTypeHeaderFields,
        taskTypeFields
    }));
};

export const getTaskPrint = (companyId: string, id: string, rowAmount: number, selectedRowIds: string[], printRowInformation: boolean): Promise<string> => {
    return postFileApiCall("api/task/print", JSON.stringify({
        taskId: id,
        companyId,
        rowAmount,
        selectedRowIds,
        printRowInformation
    }));
};
// #endregion TaskPage

// #region Report
export const getTaskPageReportItems = (companyId: string): Promise<ReportItems> => {
    const operationId = "getTaskPageReportItems" + (companyId ?? "");
    commonAborts.abort(operationId);
    return getApiCall("api/task/reports?id=" + (companyId ?? ""), ReportItems, (id: string) => {
        commonAborts.add(operationId, id);
    }).finally(() => {
        commonAborts.remove(operationId);
    });
};

export const getReportGenerated = (companyId: string, reportId: string, reportName: string, thrashBin: boolean, unitIds: string[],
    stateIds: string[], priorityIds: string[], taskTypeIds: string[], handlerUserCodes: string[], personInChargeUserCodes: string[],
    taskTypeHeaderFields: string, taskTypeFields: string, parameterValues: IReportParameterValue[]): Promise<ReportGenerated2> => {
    reportAborts.abortAll();
    chartAborts.abortAll();
    return postApiCall("api/task/createreport", ReportGenerated2, JSON.stringify({
        companyId,
        reportId,
        reportName,
        thrashBin,
        unitIds,
        stateIds,
        priorityIds,
        taskTypeIds,
        handlerUserCodes,
        personInChargeUserCodes,
        taskTypeHeaderFields,
        taskTypeFields,
        parameterValues
    }), (abortId: string) => {
        reportAborts.add(reportId, abortId);
    }).finally(() => {
        reportAborts.remove(reportId);
    });
};

export const getChartData = (companyId: string, chartName: string, panelId: string, unitIds: string[], stateIds: string[], priorityIds: string[], taskTypeIds: string[], handlerUserCodes: string[], personInChargeUserCodes: string[],
    taskTypeHeaderFields: string, taskTypeFields: string): Promise<DashboardChartData> => {
    const taskTypeChartId = (taskTypeIds.length ? taskTypeIds[0] : Base.emptyGuid) + "#" + chartName + "#" + panelId;
    chartAborts.abort(taskTypeChartId);
    return postApiCall("api/task/getchartdata", DashboardChartData, JSON.stringify({
        companyId,
        chartName,
        unitIds,
        stateIds,
        priorityIds,
        taskTypeIds,
        handlerUserCodes,
        personInChargeUserCodes,
        taskTypeHeaderFields,
        taskTypeFields,
        //parameterValues
    }), (abortId: string) => {
        chartAborts.add(taskTypeChartId, abortId);
    }).finally(() => {
        chartAborts.remove(taskTypeChartId);
    });
};

export const getChartDataById = (companyId: string, id: string): Promise<DashboardChartData> => {
    const taskTypeChartId = id;
    chartAborts.abort(taskTypeChartId);
    return postApiCall("api/task/getchartdatabyid", DashboardChartData, JSON.stringify({
        companyId,
        id
    }), (abortId: string) => {
        chartAborts.add(taskTypeChartId, abortId);
    }).finally(() => {
        chartAborts.remove(taskTypeChartId);
    });
};

// #endregion Report

// #region Dashboard
export const saveTaskPanels = (panels: IPagePanel[]): Promise<ApiSuccess> => {
    return postApiCall("api/task/savepanels", ApiSuccess, JSON.stringify({ panels }));
};

export const saveTaskChartPanels = (panels: ISavedChartPanel[]): Promise<ApiSuccess> => {
    return postApiCall("api/task/savechartpanels", ApiSuccess, JSON.stringify({ panels }));
};

export const getTaskToDoItems = (): Promise<FrontToDoItems> => {
    const operationId = "getTaskToDoItems";
    statisticAborts.abort(operationId);
    return getApiCall("api/task/todoitems", FrontToDoItems, (id: string) => {
        statisticAborts.add(operationId, id);
    }).finally(() => {
        statisticAborts.remove(operationId);
    });
};

export const getSharedPanels = (companyId: string): Promise<SharedPanels> => {
    return getApiCall("api/task/sharedpanels?id=" + companyId, SharedPanels);
};

export const saveSharedPanel = (companyId: string, orgSharedPanelId: string, panel: ISharedPanelSave): Promise<ApiSuccess> => {
    return postApiCall("api/task/savesharedpanel", ApiSuccess, JSON.stringify({ companyId, orgSharedPanelId, panel }));
};

export const getSharePanelUserGroupListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, groupType: number,
    ignoreUserGroupCodes: string[], requireUserGroupCodes: string[]): Promise<UserGroupListItems> => {
    return postApiCall("api/task/getsharepanelusergrouplistitems", UserGroupListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        groupType: groupType || 0,
        ignoreUserGroupCodes: ignoreUserGroupCodes || [],
        requireUserGroupCodes: requireUserGroupCodes || []
    }));
};
// #endregion Dashboard

// #region Module Permissions
export const getUnitNodePermissions = (companyId: string): Promise<UnitNodePermissions> => {
    return getApiCall("api/task/module/permissions?id=" + companyId, UnitNodePermissions);
};

export const saveUnitNodePermissions = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/task/module/savepermissions", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module Permissions

// #region Module units
export const getUnits = (companyId: string): Promise<ModuleUnits> => {
    return getApiCall("api/task/module/units?id=" + companyId, ModuleUnits);
};

export const saveUnits = (obj: any): Promise<UnitModuleSaveResult> => {
    return postApiCall("api/task/module/saveunits", UnitModuleSaveResult, JSON.stringify(obj));
};
// #endregion Module units

// #region Module task priorities
export const getTaskPriorityListItems = (companyId: string, taskTypeId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<TaskPriorityListItems> => {
    return postApiCall("api/task/module/taskpriority/list", TaskPriorityListItems, JSON.stringify({
        companyId,
        taskTypeId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};

export const getTaskPriorityEdit = (companyId: string, id: string): Promise<TaskPriorityEdit> => {
    return getApiCall("api/task/module/taskpriority/edit?id=" + id + "&companyId=" + companyId, TaskPriorityEdit);
};

export const saveTaskPriorityEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/task/module/taskpriority/save", SaveSuccess, JSON.stringify(obj));
};

export const removeTaskPriorities = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/task/module/taskpriority/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module task priorities

// #region Module task states
export const getTaskStateListItems = (companyId: string, taskTypeId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<TaskStateListItems> => {
    return postApiCall("api/task/module/taskstate/list", TaskStateListItems, JSON.stringify({
        companyId,
        taskTypeId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};

export const getTaskStateEdit = (companyId: string, id: string): Promise<TaskStateEdit> => {
    return getApiCall("api/task/module/taskstate/edit?id=" + id + "&companyId=" + companyId, TaskStateEdit);
};

export const saveTaskStateEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/task/module/taskstate/save", SaveSuccess, JSON.stringify(obj));
};

export const removeTaskStates = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/task/module/taskstate/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module task states

// #region Module task types
export const getTaskTypeListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, ignoreTaskTypesOfCompanyId: string): Promise<TaskTypeListItems> => {
    return postApiCall("api/task/module/tasktype/list", TaskTypeListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        ignoreTaskTypesOfCompanyId
    }));
};

export const getTaskTypeNew = (companyId: string): Promise<TaskTypeNew> => {
    return getApiCall("api/task/module/tasktype/new?id=" + companyId, TaskTypeNew);
};

export const getTaskTypeEdit = (companyId: string, id: string, addToCompany: boolean): Promise<TaskTypeEdit> => {
    return getApiCall("api/task/module/tasktype/edit?id=" + id + "&companyId=" + companyId + "&addToCompany=" + addToCompany, TaskTypeEdit);
};

export const saveTaskTypeEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/task/module/tasktype/save", SaveSuccess, JSON.stringify(obj));
};

export const removeTaskTypes = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/task/module/tasktype/remove", ApiSuccess, JSON.stringify(obj));
};

export const getTaskTypePreviewPrintHtml = (obj: any): Promise<TaskTypePrintHtml> => {
    return postApiCall("api/task/module/tasktype/previewprinthtml", TaskTypePrintHtml, JSON.stringify(obj));
};

export const getTaskTypePreviewPrint = (obj: any): Promise<string> => {
    return postFileApiCall("api/task/module/tasktype/previewprint", JSON.stringify(obj));
};

export const getTaskTypeRowPreviewPrintHtml = (obj: any): Promise<TaskTypePrintHtml> => {
    return postApiCall("api/task/module/tasktyperow/previewprinthtml", TaskTypePrintHtml, JSON.stringify(obj));
};

export const getTaskTypeRowPreviewPrint = (obj: any): Promise<string> => {
    return postFileApiCall("api/task/module/tasktyperow/previewprint", JSON.stringify(obj));
};
// #endregion Module task types

// #region Module task type units
export const taskTypeAddUnits = (companyId: string, taskTypeId: string, unitIds: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/task/module/tasktype/addunits", ApiSuccess, JSON.stringify({
        companyId,
        taskTypeId,
        unitIds
    }));
};

export const taskTypeRemoveUnits = (companyId: string, taskTypeId: string, unitIds: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/task/module/tasktype/removeunits", ApiSuccess, JSON.stringify({
        companyId,
        taskTypeId,
        unitIds
    }));
};
// #endregion Module task type units

// #region Edit
export const getTaskEdit = (companyId: string, taskId: string, lockTask: boolean): Promise<TaskEdit> => {
    return getApiCall("api/task/edit?id=" + taskId + "&companyId=" + companyId + "&lockTask=" + lockTask, TaskEdit);
};

export const getTaskEditTypeItem = (companyId: string, taskTypeId: string): Promise<TaskEditTypeItem> => {
    return getApiCall("api/task/editTypeItem?id=" + taskTypeId + "&companyId=" + companyId, TaskEditTypeItem);
};

export const getTaskEditUnitData = (companyId: string, unitId: string): Promise<TaskEditUnitData> => {
    return getApiCall("api/task/editunitdata?id=" + unitId + "&companyId=" + companyId, TaskEditUnitData);
};

export const getTaskEditTaskTypeData = (companyId: string, taskTypeId: string): Promise<TaskEditTaskTypeData> => {
    return getApiCall("api/task/edittasktypedata?id=" + taskTypeId + "&companyId=" + companyId, TaskEditTaskTypeData);
};

export const saveTaskEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/task/editsave", SaveSuccess, JSON.stringify(obj));
};

export const removeTask = (companyId: string, taskId: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/remove?id=" + taskId + "&companyId=" + companyId, ApiSuccess);
};

export const moveTaskToTrashBin = (companyId: string, taskId: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/movetotrash?id=" + taskId + "&companyId=" + companyId, ApiSuccess);
};

export const restoreTaskFromTrashBin = (companyId: string, taskId: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/restorefromtrash?id=" + taskId + "&companyId=" + companyId, ApiSuccess);
};

export const emptyTrashBin = (companyId: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/emptytrash?id=" + companyId, ApiSuccess);
};

export const archiveTask = (companyId: string, taskId: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/archive?id=" + taskId + "&companyId=" + companyId, ApiSuccess);
};

export const restoreTaskFromArchive = (companyId: string, taskId: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/restorefromarchive?id=" + taskId + "&companyId=" + companyId, ApiSuccess);
};

export const sendTaskReadReceipt = (companyId: string, id: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/sendreadreceipt", ApiSuccess, JSON.stringify({
        companyId,
        id
    }));
};

export const downloadInfoDocument = (id: string): Promise<string> => {
    return getFileApiCall("api/task/downloadinfo?id=" + id);
};
// #endregion Edit

// #region Edit Handlers
export const getHandlersSelectUserListItems = (companyId: string, taskId: string, unitId: string, pageSize: number, page: number, filter: string,
    ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, doNotIgnoreRecordUserConnectedUserCode: string,
    sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
    return postApiCall("api/task/selecthandlers", UserListItems, JSON.stringify({
        companyId,
        taskId,
        unitId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        ignoreUserIds,
        ignoreUserCodes,
        ignoreRecordUserConnected,
        doNotIgnoreRecordUserConnectedUserCode,
        activityState
    }));
};

export const taskEditAddHandlers = (companyId: string, taskId: string, taskHandlerType: TaskHandlerType, userCodes: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/task/editaddhandlers", ApiSuccess, JSON.stringify({
        companyId,
        taskId,
        taskHandlerType,
        userCodes
    }));
};

export const taskEditRemoveHandlers = (companyId: string, taskId: string, taskHandlerType: TaskHandlerType, userCodes: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/task/editremovehandlers", ApiSuccess, JSON.stringify({
        companyId,
        taskId,
        taskHandlerType,
        userCodes
    }));
};
// #endregion Edit Handlers

// #region Edit Task Data Row Attachment
export const taskEditRemoveAttachments = (data: any): Promise<ApiSuccess> => {
    return postApiCall("api/task/editremoveattachments", ApiSuccess, JSON.stringify(data));
};

export const taskEditAddAttachments = (data: any): Promise<SaveSuccessItems> => {
    return postApiCall("api/task/editaddattachments", SaveSuccessItems, JSON.stringify(data));
};
// #endregion Edit Task Data Row Attachment

// #region TaskDataRow
export const getTaskDataRowListItems = (companyId: string, taskId: string, pageSize: number, page: number, taskDataRowFilterFields: string = null): Promise<TaskDataRowListItems> => {
    return postApiCall("api/task/datarow/list", TaskDataRowListItems, JSON.stringify({
        companyId,
        taskId,
        pageSize,
        page,
        taskDataRowFilterFields
    }));
};

export const getTaskDataRowDefaultValues = (companyId: string, taskId: string): Promise<TaskDataRowDefaultValues> => {
    return getApiCall("api/task/datarow/new/defaultvalues?taskId=" + taskId + "&companyId=" + companyId, TaskDataRowDefaultValues);
};

export const getTaskDataRowEdit = (companyId: string, taskId: string, taskDataRowId: string): Promise<TaskDataRowEdit> => {
    return getApiCall("api/task/datarow/edit?id=" + taskDataRowId + "&taskId=" + taskId + "&companyId=" + companyId, TaskDataRowEdit);
};

export const saveTaskDataRowEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/task/datarow/save", SaveSuccess, JSON.stringify(obj));
};

export const removeTaskDataRow = (companyId: string, taskId: string, taskDataRowId: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/datarow/remove?id=" + taskDataRowId + "&taskId=" + taskId + "&companyId=" + companyId, ApiSuccess);
};

export const getTaskDataRowPrint = (companyId: string, taskId: string, taskDataRowId: string): Promise<string> => {
    return getFileApiCall("api/task/datarow/print?id=" + taskDataRowId + "&taskId=" + taskId + "&companyId=" + companyId);
};
// #endregion TaskDataRow

// #region TaskDataRowImport
export const getTaskDataRowImportTemplate = (companyId: string, taskId: string, taskTypeId: string): Promise<string> => {
    return getFileApiCall("api/task/datarow/importtemplate?id=" + taskTypeId + "&taskId=" + taskId + "&companyId=" + companyId);
};

export const uploadTaskDataRowImportFile = (obj: any): Promise<TaskDataRowListItems> => {
    return postApiCall("api/task/datarow/uploadimportfile", TaskDataRowListItems, JSON.stringify(obj));
};

export const importTaskDataRows = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/task/datarow/import", ApiSuccess, JSON.stringify(obj));
};
// #endregion TaskDataRowImport

// #region Task CalendarTasks
export const getCalendarTaskListItems = (companyId: string, taskId: string, pageSize: number, page: number): Promise<CalendarTaskListItems> => {
    return postApiCall("api/task/calendartask/list", CalendarTaskListItems, JSON.stringify({
        companyId,
        recordId: taskId,
        pageSize,
        page,
    }));
};

export const getCalendarTaskEdit = (companyId: string, calendarTaskId: string, taskId: string): Promise<CalendarTaskEdit> => {
    return getApiCall("api/task/calendartask/edit?id=" + calendarTaskId + "&companyId=" + companyId + "&taskId=" + taskId, CalendarTaskEdit);
};

export const saveCalendarTaskEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/task/calendartask/save", SaveSuccess, JSON.stringify(obj));
};

export const removeCalendarTask = (companyId: string, calendarTaskId: string, taskId: string): Promise<ApiSuccess> => {
    return postApiCall("api/task/calendartask/remove?id=" + calendarTaskId + "&companyId=" + companyId + "&taskId=" + taskId, ApiSuccess);
};
// #endregion Task CalendarTasks
